import { useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * A controlled tab set with the ARIA wiring the pattern requires: roving tabindex, arrow
 * keys, and a panel bound to its tab. Controlled deliberately — the URL, not local state,
 * is the source of truth on the pages that use it.
 */
export function Tabs({ items, activeId, onChange, className }: TabsProps) {
  const base = useId();

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const next = items[(index + delta + items.length) % items.length];
    if (next) onChange(next.id);
  }

  const active = items.find((item) => item.id === activeId) ?? items[0];

  return (
    <div className={className}>
      <div role="tablist" aria-label="Sections" className="flex gap-1 border-b border-line">
        {items.map((item, index) => {
          const selected = item.id === active?.id;
          return (
            <button
              key={item.id}
              id={`${base}-tab-${item.id}`}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={`${base}-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(item.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                selected
                  ? "border-primary text-primary"
                  : "border-transparent text-ink-muted hover:text-ink",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {active ? (
        <div
          id={`${base}-panel-${active.id}`}
          role="tabpanel"
          aria-labelledby={`${base}-tab-${active.id}`}
          tabIndex={0}
          className="pt-5 focus-visible:outline-none"
        >
          {active.content}
        </div>
      ) : null}
    </div>
  );
}
